import { InterestPeriodDefaultBehavior, } from '@debbie/common/dist/economy/case-vouchers/index.js';
import { defaultLastBankDayStart, lastDanishBankDayOfMonth, } from '@debbie/common/dist/economy/helpers/dates/danish-bank-days.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { DEFAULT_FEE_VOUCHER_TYPE_ID, Interval, resolvePaymentPlansConfig, } from '@debbie/common/dist/economy/payment-plans/index.js';
import { VoucherCategory } from '@debbie/common/dist/economy/vouchers/index.js';
import { formatNumber } from '@debbie/common/dist/index.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import { useVoucherTypes } from '@debbie/cortex/dist/voucher-types/index.js';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import Alert from '../Alert.js';
import DateInput from '../DateInput.js';
import { Panel } from '../index.js';
import { Checkbox, InputGroup } from '../index.js';
import MoneyInput from '../MoneyInput.js';
import { Section } from '../PanelSection.js';
import Select from '../Select.js';
import Well from '../Well.js';
import IntervalInput from './components/IntervalInput.js';
import MethodInput from './components/MethodInput.js';
import SettlementInput from './components/SettlementInput.js';
const PaymentPlanEditor = (props) => {
    const { selectPlan, caseVouchers } = props;
    const lang = useSelector((s) => s.lang);
    const documentsLoader = useDocumentsByReference(props.caseId);
    const documents = h.hasData(documentsLoader) ? documentsLoader.data : null;
    const tenant = useTenant();
    const creditor = props.creditor;
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(props.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const conditionsLoader = useCaseConditions(props.caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    const voucherTypesLoadable = useVoucherTypes();
    const voucherTypes = h.hasData(voucherTypesLoadable)
        ? voucherTypesLoadable.data
        : [];
    const costVoucherTypes = voucherTypes.filter((vt) => vt.category === VoucherCategory.COST);
    const [moreSettings, setMoreSettings] = useState(false);
    const [interestFreeEnabled, setInterestFreeEnabled] = useState(false);
    const [interestFreeFrom, setInterestFreeFrom] = useState(null);
    const [interestFreeTo, setInterestFreeTo] = useState(undefined);
    const [interestFreeOnDefault, setInterestFreeOnDefault] = useState(null);
    const resolvedInterestFreeOnDefault = interestFreeOnDefault ??
        resolvePaymentPlansConfig(creditor?.paymentPlansConfig, tenant?.paymentPlansConfig).interestFreeOnDefault ??
        InterestPeriodDefaultBehavior.KeepFrozen;
    const [upfrontEnabled, setUpfrontEnabled] = useState(false);
    const [amountUpfront, setAmountUpfront] = useState(10000);
    const [amountUpfrontRaw, setAmountUpfrontRaw] = useState(10000);
    const [upfrontDate, setUpfrontDate] = useState(moment());
    const [settlementEnabled, setSettlementEnabled] = useState(false);
    const [settlementTotal, setSettlementTotal] = useState(0);
    const enableSettlement = (enabled) => {
        setSettlementEnabled(enabled);
        if (enabled && settlementTotal <= 0) {
            const balance = -helpers.overview.balance(caseVouchers);
            setSettlementTotal(Math.round(balance * 0.6));
        }
    };
    const [byAmount, setByAmount] = useState(true);
    const [amount, setAmount] = useState(10000);
    const [amountRaw, setAmountRaw] = useState(10000);
    const [duration, setDuration] = useState(2);
    const [durationRaw, setDurationRaw] = useState('2');
    const [scheduledAmountChanges, setScheduledAmountChanges] = useState([]);
    const [scheduledAmountChangesRaw, setScheduledAmountChangesRaw] = useState([]);
    const [interval, setInterval] = useState(Interval.LASTBANKDAYMONTH);
    const [initialFeeAmount, setInitialFeeAmount] = useState(props.defaultFeeStructure?.initialFee?.amount ?? 0);
    const [initialFeeAmountRaw, setInitialFeeAmountRaw] = useState(props.defaultFeeStructure?.initialFee?.amount ?? 0);
    const [initialFeeVoucherTypeId, setInitialFeeVoucherTypeId] = useState(props.defaultFeeStructure?.initialFee?.voucherTypeId ??
        DEFAULT_FEE_VOUCHER_TYPE_ID);
    const [perEntryFeeAmount, setPerEntryFeeAmount] = useState(props.defaultFeeStructure?.perEntryFee?.amount ?? 0);
    const [perEntryFeeAmountRaw, setPerEntryFeeAmountRaw] = useState(props.defaultFeeStructure?.perEntryFee?.amount ?? 0);
    const [perEntryFeeVoucherTypeId, setPerEntryFeeVoucherTypeId] = useState(props.defaultFeeStructure?.perEntryFee?.voucherTypeId ??
        DEFAULT_FEE_VOUCHER_TYPE_ID);
    const snapStartDate = (d) => interval === Interval.LASTBANKDAYMONTH ? lastDanishBankDayOfMonth(d) : d;
    const isLastBankDayOfMonth = (date) => {
        const lastBankDay = lastDanishBankDayOfMonth(moment(date));
        return (date.getFullYear() === lastBankDay.year() &&
            date.getMonth() === lastBankDay.month() &&
            date.getDate() === lastBankDay.date());
    };
    const dateFilter = interval === Interval.LASTBANKDAYMONTH ? isLastBankDayOfMonth : undefined;
    const [startDate, setStartDate] = useState(defaultLastBankDayStart(moment()));
    const [trigger, setTrigger] = useState(null);
    useEffect(() => {
        const balance = -helpers.overview.balance(props.caseVouchers);
        const _amount = Math.floor(balance / 3 / 10000) * 10000 || 10000;
        setAmountRaw(_amount);
        setAmount(_amount);
        setTrigger(Math.random());
    }, [caseVouchers]);
    useEffect(() => {
        if (caseVouchers && trigger) {
            setPlan();
        }
    }, [
        trigger,
        documents,
        tenant,
        creditor,
        fixedDistributions,
        byAmount,
        interval,
        startDate,
        upfrontEnabled,
        amountUpfront,
        upfrontDate,
        amount,
        duration,
        scheduledAmountChanges,
        settlementEnabled,
        settlementTotal,
        interestFreeEnabled,
        interestFreeEnabled && interestFreeFrom,
        interestFreeEnabled && interestFreeTo,
        interestFreeEnabled && resolvedInterestFreeOnDefault,
        initialFeeAmount,
        initialFeeVoucherTypeId,
        perEntryFeeAmount,
        perEntryFeeVoucherTypeId,
    ]);
    const getInteretFreeFrom = () => {
        return interestFreeFrom || startDate;
    };
    const getInteretFreeTo = () => {
        if (interestFreeTo === null) {
            return null;
        }
        if (interestFreeTo) {
            return interestFreeTo;
        }
        const to = getInteretFreeFrom().clone();
        return to.add(1, 'month');
    };
    const getFeeStructure = () => {
        const feeStructure = {};
        if (initialFeeAmount > 0 && initialFeeVoucherTypeId) {
            feeStructure.initialFee = {
                amount: initialFeeAmount,
                voucherTypeId: initialFeeVoucherTypeId,
            };
        }
        if (perEntryFeeAmount > 0 && perEntryFeeVoucherTypeId) {
            feeStructure.perEntryFee = {
                amount: perEntryFeeAmount,
                voucherTypeId: perEntryFeeVoucherTypeId,
            };
        }
        return Object.keys(feeStructure).length > 0 ? feeStructure : undefined;
    };
    const setPlan = async () => {
        if (h.isError(conditionsLoader)) {
            errorNotification(lang, new Error(lang.costCeiling.failedToLoad), 'create', lang.errorEntities.paymentPlan);
            return;
        }
        if (!caseVouchers ||
            documents == null ||
            tenant == null ||
            creditor == null ||
            props.singleCase == null ||
            fixedDistributions == null ||
            conditions == null) {
            return;
        }
        const baseConfig = {
            startDate: startDate.toISOString(),
            interval,
            amountUpfront: upfrontEnabled ? amountUpfront : null,
            dateUpfront: upfrontEnabled ? upfrontDate?.toISOString() || null : null,
            interestFree: interestFreeEnabled
                ? {
                    from: getInteretFreeFrom().toISOString(),
                    to: getInteretFreeTo()?.toISOString() || null,
                    onDefault: resolvedInterestFreeOnDefault,
                }
                : null,
            feeStructure: getFeeStructure(),
            // Zod requires a positive settlementTotal, so an unset/zero draft is
            // sent as null (no settlement) rather than rejected on save.
            settlementTotal: settlementEnabled && settlementTotal > 0 ? settlementTotal : null,
        };
        let _config;
        if (byAmount) {
            _config = {
                ...baseConfig,
                method: 'amount',
                amount,
                scheduledAmountChanges: scheduledAmountChanges?.map((c) => ({
                    amount: c.amount,
                    date: c.date.toISOString(),
                })),
            };
        }
        else {
            _config = {
                ...baseConfig,
                method: 'duration',
                duration,
            };
        }
        try {
            const plan = await helpers.paymentPlans.gen(tenant, creditor, props.singleCase.economyEngine, JSON.parse(JSON.stringify(helpers.vouchers.postProcessing(caseVouchers, {
                includeIrrecoverableVouchers: false,
                includeDeletedVouchers: false,
                includeExpiredVouchers: false,
            }))), documents, fixedDistributions, _config, conditions, { projectInterest: true });
            selectPlan('CUSTOM', plan, _config);
        }
        catch (error) {
            errorNotification(lang, error, 'create', lang.errorEntities.paymentPlan);
        }
    };
    return (React.createElement("div", null,
        React.createElement(Panel, null,
            React.createElement(Section, { header: lang.paymentPlans.calculationFoundation, description: lang.paymentPlans.calculationFoundationDescription },
                React.createElement(MethodInput, { byAmount: byAmount, setByAmount: setByAmount, amount: amount, setAmount: setAmount, amountRaw: amountRaw, setAmountRaw: setAmountRaw, duration: duration, setDuration: setDuration, durationRaw: durationRaw, setDurationRaw: setDurationRaw, scheduledAmountChanges: scheduledAmountChanges, setScheduledAmountChanges: setScheduledAmountChanges, scheduledAmountChangesRaw: scheduledAmountChangesRaw, setScheduledAmountChangesRaw: setScheduledAmountChangesRaw, startDate: startDate, snapStartDate: snapStartDate, dateFilter: dateFilter })),
            React.createElement(Section, { header: lang.paymentPlans.startDate, description: lang.paymentPlans.startDateDescription },
                React.createElement(DateInput, { placeholder: lang.paymentPlans.startDate, value: startDate, onChange: (d) => {
                        setStartDate(snapStartDate(d));
                    }, minDate: new Date(), filterDate: dateFilter, allowNull: false })),
            React.createElement(Section, { header: lang.paymentPlans.settlement, description: lang.paymentPlans.settlementDescription },
                React.createElement(SettlementInput, { caseVouchers: caseVouchers, enabled: settlementEnabled, setEnabled: enableSettlement, total: settlementTotal, setTotal: setSettlementTotal })),
            moreSettings ? (React.createElement(React.Fragment, null,
                React.createElement(Section, { header: lang.paymentPlans.upfrontPayment, description: lang.paymentPlans.upfrontPaymentDescription },
                    React.createElement(InputGroup, null,
                        React.createElement(Checkbox, { checked: upfrontEnabled, onChange: (e) => {
                                setUpfrontEnabled(e.target.checked);
                            } }),
                        React.createElement(MoneyInput, { value: amountUpfrontRaw, disabled: !upfrontEnabled, placeholder: lang.paymentPlans.upfrontPayment, onChange: (e) => {
                                setAmountUpfrontRaw(e);
                            }, onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    setAmountUpfront(amountUpfrontRaw);
                                }
                            }, onBlur: (e) => {
                                setAmountUpfront(amountUpfrontRaw);
                            }, button: amountUpfrontRaw !== amountUpfront
                                ? {
                                    value: lang.paymentPlans.save,
                                    onClick: () => {
                                        setAmountUpfront(amountUpfrontRaw);
                                    },
                                }
                                : undefined })),
                    React.createElement(DateInput, { placeholder: lang.paymentPlans.upfrontPaymentDate, value: upfrontDate, onChange: (d) => {
                            setUpfrontDate(d);
                        }, minDate: new Date(), allowNull: true, disabled: !upfrontEnabled })),
                React.createElement(Section, { header: lang.paymentPlans.interval, description: lang.paymentPlans.intervalDescription },
                    React.createElement(IntervalInput, { interval: interval, onChange: (interval) => {
                            setInterval(interval);
                            if (interval === Interval.LASTBANKDAYMONTH) {
                                setStartDate((d) => lastDanishBankDayOfMonth(d));
                            }
                        } })),
                React.createElement(Section, { header: lang.paymentPlans.interestFree, description: lang.paymentPlans.interestFreeDescription },
                    React.createElement(Checkbox, { checked: interestFreeEnabled, onChange: (e) => {
                            setInterestFreeEnabled(e.target.checked);
                        } }),
                    React.createElement(DateInput, { placeholder: lang.paymentPlans.interestFreeFrom, value: getInteretFreeFrom(), onChange: (d) => {
                            setInterestFreeFrom(d);
                        }, maxDate: interestFreeTo ? interestFreeTo.toDate() : undefined, allowNull: true, disabled: !interestFreeEnabled }),
                    React.createElement(DateInput, { placeholder: lang.paymentPlans.interestFreeTo, value: getInteretFreeTo(), onChange: (d) => {
                            setInterestFreeTo(d);
                        }, minDate: interestFreeFrom ? interestFreeFrom.toDate() : undefined, allowNull: true, disabled: !interestFreeEnabled }),
                    React.createElement(Select, { value: resolvedInterestFreeOnDefault, onChange: (e) => {
                            setInterestFreeOnDefault(e.target.value);
                        }, disabled: !interestFreeEnabled, label: lang.paymentPlans.interestFreeOnDefault },
                        React.createElement("option", { value: InterestPeriodDefaultBehavior.KeepFrozen }, lang.paymentPlans.interestFreeOnDefaultKeepFrozen),
                        React.createElement("option", { value: InterestPeriodDefaultBehavior.Reapply }, lang.paymentPlans.interestFreeOnDefaultReapply))),
                React.createElement(Section, { header: lang.paymentPlans.fees, description: lang.paymentPlans.feesDescription },
                    React.createElement(Well, null,
                        React.createElement(FeeTitle, null, lang.paymentPlans.initialFee),
                        React.createElement(FeeDescription, null, lang.paymentPlans.initialFeeDescription),
                        React.createElement(MoneyInput, { value: initialFeeAmountRaw, placeholder: lang.paymentPlans.amount, onChange: (e) => {
                                setInitialFeeAmountRaw(e);
                            }, onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    setInitialFeeAmount(initialFeeAmountRaw);
                                }
                            }, onBlur: () => {
                                setInitialFeeAmount(initialFeeAmountRaw);
                            }, button: initialFeeAmountRaw !== initialFeeAmount
                                ? {
                                    value: lang.paymentPlans.save,
                                    onClick: () => {
                                        setInitialFeeAmount(initialFeeAmountRaw);
                                    },
                                }
                                : undefined }),
                        React.createElement(Select, { value: initialFeeVoucherTypeId || '', onChange: (e) => {
                                setInitialFeeVoucherTypeId(e.target.value || null);
                            }, disabled: initialFeeAmount <= 0, label: lang.paymentPlans.voucherType },
                            React.createElement("option", { value: "" }, lang.paymentPlans.selectVoucherType),
                            costVoucherTypes.map((vt) => (React.createElement("option", { key: vt.voucherTypeId, value: vt.voucherTypeId }, lang.economyVoucherTypeLabels[vt.voucherTypeId] ??
                                vt.label)))),
                        initialFeeAmount > 0 && !initialFeeVoucherTypeId && (React.createElement(Alert, { size: "small", message: lang.paymentPlans.selectVoucherTypeRequired }))),
                    React.createElement(FeeWell, null,
                        React.createElement(FeeTitle, null, lang.paymentPlans.perEntryFee),
                        React.createElement(FeeDescription, null, lang.paymentPlans.perEntryFeeDescription),
                        React.createElement(MoneyInput, { value: perEntryFeeAmountRaw, placeholder: lang.paymentPlans.amount, onChange: (e) => {
                                setPerEntryFeeAmountRaw(e);
                            }, onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    setPerEntryFeeAmount(perEntryFeeAmountRaw);
                                }
                            }, onBlur: () => {
                                setPerEntryFeeAmount(perEntryFeeAmountRaw);
                            }, button: perEntryFeeAmountRaw !== perEntryFeeAmount
                                ? {
                                    value: lang.paymentPlans.save,
                                    onClick: () => {
                                        setPerEntryFeeAmount(perEntryFeeAmountRaw);
                                    },
                                }
                                : undefined }),
                        React.createElement(Select, { value: perEntryFeeVoucherTypeId || '', onChange: (e) => {
                                setPerEntryFeeVoucherTypeId(e.target.value || null);
                            }, disabled: perEntryFeeAmount <= 0, label: lang.paymentPlans.voucherType },
                            React.createElement("option", { value: "" }, lang.paymentPlans.selectVoucherType),
                            costVoucherTypes.map((vt) => (React.createElement("option", { key: vt.voucherTypeId, value: vt.voucherTypeId }, lang.economyVoucherTypeLabels[vt.voucherTypeId] ??
                                vt.label)))),
                        perEntryFeeAmount > 0 && !perEntryFeeVoucherTypeId && (React.createElement(Alert, { size: "small", message: lang.paymentPlans.selectVoucherTypeRequired })))))) : (React.createElement(MoreSettingsContainer, null,
                React.createElement(MoreSettingsLink, { onClick: () => setMoreSettings(true) }, lang.paymentPlans.moreSettings),
                (initialFeeAmount > 0 || perEntryFeeAmount > 0) && (React.createElement(FeeInfoText, null,
                    "(",
                    lang.paymentPlans.fees,
                    ":",
                    ' ',
                    [
                        initialFeeAmount > 0 &&
                            `${lang.paymentPlans.initialFee} ${formatNumber(initialFeeAmount)}`,
                        perEntryFeeAmount > 0 &&
                            `${lang.paymentPlans.perEntryFee} ${formatNumber(perEntryFeeAmount)}`,
                    ]
                        .filter(Boolean)
                        .join(', '),
                    ")")))))));
};
export default PaymentPlanEditor;
const MoreSettingsContainer = styled.p `
  margin-bottom: 0;
`;
const MoreSettingsLink = styled.span `
  cursor: pointer;
  text-transform: uppercase;
  color: ${THEME.COLOR_ACCENT};
  font-weight: 900;
`;
const FeeInfoText = styled.span `
  color: #999;
  margin-left: 8px;
  font-size: 0.8em;
  text-transform: none;
  font-weight: normal;
`;
const FeeTitle = styled.h4 `
  margin-top: 0;
`;
const FeeDescription = styled.p `
  font-size: 0.85em;
  color: #666;
  margin-bottom: 10px;
`;
const FeeWell = styled(Well) `
  margin-top: 15px;
`;
//# sourceMappingURL=PaymentPlanEditor.js.map